import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useEffect, useState } from "react";

/**
 * GlowBackground — subtle "fuel pathway" network with mouse-follow parallax.
 * Pure CSS/SVG, solid dark base (no AI-slop gradients). Sits behind hero content.
 */
export const GlowBackground = ({ className = "" }) => {
  const ref = useRef(null);
  const [mouse, setMouse] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const handle = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 2;
      const y = (e.clientY / window.innerHeight - 0.5) * 2;
      setMouse({ x, y });
    };
    window.addEventListener("mousemove", handle);
    return () => window.removeEventListener("mousemove", handle);
  }, []);

  return (
    <div ref={ref} className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`} aria-hidden="true">
      {/* radial ember glow */}
      <div className="ff-radial-glow absolute inset-0" />
      {/* grain */}
      <div className="ff-grain absolute inset-0 opacity-[0.03]" />

      {/* fuel-pathway lines */}
      <motion.svg
        className="absolute left-1/2 top-0 h-[140%] w-[140%] -translate-x-1/2 opacity-40"
        viewBox="0 0 1200 800"
        fill="none"
        animate={{ x: mouse.x * -18, y: mouse.y * -12 }}
        transition={{ type: "spring", stiffness: 40, damping: 20 }}
      >
        <defs>
          <linearGradient id="pathGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#FF4B22" stopOpacity="0" />
            <stop offset="50%" stopColor="#FF4B22" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#2FAE91" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[120, 260, 400, 560, 720].map((y, i) => (
          <motion.path
            key={i}
            d={`M-50 ${y} C 300 ${y - 80}, 500 ${y + 90}, 800 ${y - 40} S 1250 ${y + 60}, 1250 ${y}`}
            stroke="url(#pathGrad)"
            strokeWidth="1.2"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 1 }}
            transition={{ duration: 2.2, delay: 0.3 + i * 0.15, ease: "easeInOut" }}
          />
        ))}
        {[
          [200, 180], [640, 260], [980, 200], [420, 520], [880, 600], [180, 660],
        ].map(([cx, cy], i) => (
          <motion.circle
            key={i}
            cx={cx}
            cy={cy}
            r="3.5"
            fill="#FF4B22"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: [0, 1.2, 1], opacity: [0, 1, 0.7] }}
            transition={{ duration: 1.4, delay: 0.8 + i * 0.12, repeat: Infinity, repeatType: "reverse", repeatDelay: 1.6 }}
          />
        ))}
      </motion.svg>
    </div>
  );
};

/**
 * ParallaxWrap — moves children slightly on scroll for depth.
 */
export const ParallaxWrap = ({ children, offset = 60, className = "" }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [offset, -offset]);
  return (
    <motion.div ref={ref} style={{ y }} className={className}>
      {children}
    </motion.div>
  );
};

export default GlowBackground;
